import {
  buildUnifiedChatRequest,
  resolveUnifiedChatRequestBillingSource,
  type UnifiedChatSurface,
} from "../chat-runtime";

function extractCompletionText(payload: unknown): string {
  if (!payload || typeof payload !== "object") return "";

  const choices = (payload as { choices?: unknown }).choices;
  if (!Array.isArray(choices) || choices.length === 0) return "";

  const first = choices[0];
  if (!first || typeof first !== "object") return "";

  const message = (first as { message?: unknown }).message;
  if (!message || typeof message !== "object") return "";

  const content = (message as { content?: unknown }).content;
  if (typeof content === "string") return content.trim();
  if (Array.isArray(content)) {
    return content
      .map((part) => {
        if (!part || typeof part !== "object") return "";
        const text = (part as { text?: unknown }).text;
        return typeof text === "string" ? text : "";
      })
      .join(" ")
      .trim();
  }

  return "";
}

export async function requestContextualChatReply(params: {
  systemPrompt: string;
  userPrompt: string;
  surface?: UnifiedChatSurface;
  sessionId?: string;
  contextSummary?: string;
  uiContext?: Record<string, unknown>;
  provenance?: Record<string, unknown>;
}): Promise<{
  assistantReply: string;
  requestModel: string;
  responseModel: string;
} | null> {
  const requestModel = "auto";
  try {
    const [aiSetup, inferenceStatus] = await Promise.all([
      fetch("/api/connect/ai")
        .then(async (res) => (res.ok ? (res.json() as Promise<Record<string, unknown>>) : null))
        .catch(() => null),
      fetch("/api/inference/status")
        .then(async (res) => (res.ok ? (res.json() as Promise<Record<string, unknown>>) : null))
        .catch(() => null),
    ]);
    const res = await fetch("/api/chat/completions", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        buildUnifiedChatRequest({
          model: requestModel,
          sessionId: params.sessionId,
          billingSource: resolveUnifiedChatRequestBillingSource({
            preferredSource:
              typeof aiSetup?.preferredSource === "string" ? aiSetup.preferredSource : undefined,
            connectedApiProviders: Array.isArray(aiSetup?.connectedApiProviders)
              ? aiSetup.connectedApiProviders
              : undefined,
            mode: typeof inferenceStatus?.mode === "string" ? inferenceStatus.mode : undefined,
            routeTruth:
              inferenceStatus && typeof inferenceStatus === "object"
                ? ((inferenceStatus as { routeTruth?: { billingSource?: unknown } }).routeTruth ??
                  null)
                : null,
          }),
          stream: false,
          surface: params.surface ?? "contextual_chat",
          contextSummary: params.contextSummary,
          uiContext: params.uiContext,
          provenance: params.provenance,
          messages: [
            { role: "system", content: params.systemPrompt },
            { role: "user", content: params.userPrompt },
          ],
        }),
      ),
    });
    if (!res.ok) return null;

    const json = (await res.json()) as { model?: unknown };
    const actualModelHeader = res.headers.get("x-openclaw-actual-model")?.trim() || "";
    const assistantReply = extractCompletionText(json);
    if (!assistantReply) return null;

    return {
      assistantReply,
      requestModel,
      responseModel: actualModelHeader || String(json.model || ""),
    };
  } catch {
    return null;
  }
}
